<svelte:options
	customElement={{
		tag: 'rf-canvas',
		shadow: 'open',
	}}
/>

<script module>
	/**
	 * Types
	 */
	import type { AnnotationNormalized, AnnotationPageNormalized, ContentResource } from '@iiif/presentation-3';
	import type { RFCanvasMeta } from '@src/types/reframe';
	import type { CanvasPanelWebComponent, CanvasPanelMethods } from '@src/types/canvas-panel.d.ts';
	export interface ReframeCanvasProps {
		fit?: 'contain' | 'cover' | 'auto';
		interactive?: string | boolean;
		className: string;
		'iiif-content'?: string;
		'canvas-id'?: string;
		'choice-id'?: string;
		'manifest-id'?: string;
		'move-events': string | boolean;
		'granular-move-events': string | boolean;
		src?: string;
		legend: boolean;
		debug: boolean;
		loading?: 'lazy' | 'eager' | undefined;
		width?: string | number;
		height?: string | number;
		'interactive-mode': 'free' | 'keydown';
		'in-inspector': boolean;
		'inspector-key': string;
		'inspector-hide'?: boolean;
		region?: string;
	}
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import { v4 as uuidv4 } from 'uuid';
	import classNames from 'classnames';
	import { createInternalManifest } from '@src/utils/manifest.ts';
	import '@digirati/canvas-panel-web-components';
	import { onMount, onDestroy, createEventDispatcher, tick } from 'svelte';
	import cssVariables from '@src/utils/cssvariables';
	import parseBool from '@src/utils/bool';
	import getAspectRatio from '@src/utils/aspectRatio';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import { get } from 'svelte/store';
	import { emitEvent } from '@src/utils/events';
	import inspectorStore from '@store/inspector.svelte';
	import RfInspectorControl from '@core/rf-inspector/rf-inspector-control.svelte';
	import type { NormalisedContentState } from '@iiif/helpers';
	import { waitUntilAvailable } from '@src/utils/waitUntilAvailable';

	/**
	 * Props
	 */
	let {
		className = '',
		interactive = 'false',
		fit = 'auto',
		'iiif-content': iiifContent = '',
		'move-events': moveEvents = false,
		'granular-move-events': granularMoveEvents = false,
		'canvas-id': canvasId = '',
		'manifest-id': manifestId = '',
		'choice-id': choiceId = '',
		src = '',
		debug = false,
		legend = true,
		loading = undefined,
		width = undefined,
		height = undefined,
		'interactive-mode': interactiveMode = 'free',
		'in-inspector': inInspector = false,
		'inspector-key': inspectorKey = '',
		'inspector-hide': inspectorHide = false,
		region = undefined,
	}: ReframeCanvasProps = $props();

	const meta: RFCanvasMeta = {
		uuid: uuidv4(),
		canvasId,
		manifestId,
		choiceId,
		iiifContent,
		src,
		interactive,
		fit,
		legend: {},
		getCanvasSize,
	};

	/**
	 * Variables
	 */

	let canvasPanel: CanvasPanelWebComponent | null = $state(null);
	const { element, forEachSlottedElement } = useSlottedElements();
	let observer: IntersectionObserver | null = null;
	let isReadyToLoad: boolean = $state(false);
	let isReadyToInteract: boolean = $state(false);
	let isReady: boolean = $state(false);
	let naturalWidth: number = $state(0);
	let naturalHeight: number = $state(0);
	let render: boolean = $state(true);
	let canInteract: boolean = $state(true);

	/**
	 * Derived
	 */

	let isInteractive = $derived.by(() => {
		if (iiifContent || (manifestId && canvasId) || (src && parseBool(interactive))) return true;
		else return false;
	});

	/**
	 * Methods
	 */

	const getClasses = (name: string = ''): string => {
		return classNames(name, className, `${name}--${fit}`, {
			'no-legend': !parseBool(legend),
			'no-events': !canInteract,
			'in-inspector': inInspector,
		});
	};

	/**
	 * Re-render canvas-panel component
	 */
	export const refresh = async (): Promise<void> => {
		render = false;

		await tick();

		render = true;
	};

	/**
	 * CanvasPanel methods bridge
	 */
	const callCanvasPanelMethod = (method: keyof CanvasPanelMethods, ...args: unknown[]) => {
		if (!parseBool(interactive)) {
			console.warn(`${method}(): RfCanvas must be set to interactive="true"`);
			return;
		}

		if (!isReadyToInteract) {
			console.warn(`${method}(): RfCanvas is not ready, wait for load event before calling interaction methods`);
			return;
		}

		if (canvasPanel && typeof canvasPanel[method] === 'function') {
			canvasPanel[method](...args);
		} else {
			console.warn(`${method}() does not exist on CanvasPanel`);
			return;
		}
	};

	export const zoomIn = (): void => callCanvasPanelMethod('zoomIn');
	export const zoomOut = (): void => callCanvasPanelMethod('zoomOut');
	export const goHome = (): void => callCanvasPanelMethod('goHome');
	export const goToTarget = (e: any, options: any): void => callCanvasPanelMethod('goToTarget', e, options);
	export const goFullscreen = (): void => {
		if (canvasPanel) canvasPanel.requestFullscreen?.call(canvasPanel);
	};

	export const getLegendMeta = (): object => {
		let legendMeta = {};

		forEachSlottedElement((el: any) => {
			if (el.tagName === 'RF-LEGEND') legendMeta = el.getMeta();
			if (el.tagName === 'RF-CANVAS-ANNOTATION') {
				console.log('Canvas Annotation found in canvas panel, this is deprecated, please use rf-legend instead');
			}
		});

		meta.legend = legendMeta;

		return legendMeta;
	};

	/**
	 * Attributes
	 */

	const getResponsiveAttribute = () => {
		if (fit === 'auto') return true;

		return false;
	};

	/**
	 * Determines the appropriate viewport attribute based on the `fit` mode.
	 *
	 * @returns {boolean | undefined} - Returns `true` if `fit` is 'contain' or 'cover', `false` if `fit` is 'auto', and `undefined` for any other value.
	 */
	const getViewportAttribute = (): boolean | undefined => {
		// If `fit` is 'contain' or 'cover', return `true`.
		if (fit === 'contain' || fit === 'cover') return true;

		// If `fit` is 'auto', return `false`.
		if (fit === 'auto') return false;

		// Default case: return `undefined`.
		return undefined;
	};

	/**
	 * Returns the appropriate attribute value based on lazy loading conditions.
	 *
	 * @param {string} attribute - The attribute value to be used if the element is ready to load.
	 * @returns {string} - The original attribute if the element should load, or an empty string if it's still lazy-loading.
	 */
	const getLazyAttribute = (attribute: string): string => {
		// If the element is ready to load and the loading mode is 'lazy', return the attribute.
		if (isReadyToLoad && loading === 'lazy') return attribute;
		// If the element is NOT ready to load and the loading mode is 'lazy', return an empty string.
		else if (!isReadyToLoad && loading === 'lazy') return '';

		// If the loading mode is not 'lazy', always return the attribute.
		return attribute;
	};

	const getLegendId = (): string | null => {
		if (isReady) {
			let uuid = null;
			forEachSlottedElement((el: any) => {
				if (el.getUUID) uuid = el.getUUID();
			});
			return uuid;
		}

		return null;
	};

	/**
	 * Events
	 */
	const dispatch = createEventDispatcher();

	const onAtlas = (e: any) => {
		if (debug) console.log('Event::CanvasPanel::Atlas', e);

		dispatch('atlas', e);
	};

	const onReady = (e: any) => {
		if (debug) console.log('Event::CanvasPanel::Ready', e);

		dispatch('ready', e);
	};

	const onMediaDisplayed = (e: any) => {
		if (debug) console.log('Event::CanvasPanel::MediaDisplayed', e);

		dispatch('media-displayed', e);
	};

	const onWorldReady = (e: any) => {
		if (debug) console.log('Event::CanvasPanel::WorldReady', e);

		if (fit === 'cover') canvasPanel?.setAttribute('home-cover', 'true');

		isReadyToInteract = true;

		naturalWidth = e.detail.worldWidth;
		naturalHeight = e.detail.worldHeight;

		dispatch('world-ready', e);
		emitEvent('rf-canvas-load');

		dispatch('load', { naturalWidth, naturalHeight, meta });
	};

	const onCanvasChange = (e: any) => {
		if (debug) console.log('Event::CanvasPanel::CanvasChange', e);

		isReadyToInteract = true;

		dispatch('canvas-change', e);
	};

	const onObserver = (entries: IntersectionObserverEntry[]) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				isReadyToLoad = true;
				const el = get(element);
				if (el) observer?.unobserve(el);
				if (debug) console.log('Event::RfCanvas::Entered');
			}
		});
	};

	/**
	 * Get current canvas id
	 */
	const getCurrentCanvasId = (panel: CanvasPanelWebComponent): Promise<string> => {
		return new Promise(async (resolve, reject) => {
			let canvasId = '';
			if (!canvasPanel) return reject('No panel linked');
			const contentState = (await waitUntilAvailable(() => panel.getContentState?.().contentState)) as NormalisedContentState;
			if (!contentState?.id) return reject('No current content state');
			canvasId = contentState.id.split('#')[0];
			return resolve(canvasId);
		});
	};

	function getCanvasSize() {
		return {
			width: naturalWidth,
			height: naturalHeight,
		};
	}

	export const setCollectionId = (collectionId: string) => {
		meta.collectionId = collectionId;
	};

	/**
	 * Get the best thumbnail at the given size (default to w:512 h:512)
	 */
	export const getThumbnail = async ({ width = 512, height = 512 }: { width?: number; height?: number } = {}): Promise<string> => {
		return new Promise(async (resolve, reject) => {
			// if direct props src is set, return this since we're not in a iiif context
			if (src) return resolve(src);

			const panel = await waitUntilAvailable(() => canvasPanel);
			// else check wait for canvas panel
			if (!panel) return reject('No canvas panel mounted');

			// get current canvas id (with stripped #xywh data from url) from vault
			const currentCanvasId = canvasId || (await getCurrentCanvasId(panel));
			const canvas = await waitUntilAvailable(() => panel.vault.get(currentCanvasId));
			if (!canvas || !canvas.items?.length) return reject('No canvas found');

			// get annotation page from vault
			const annoPage = panel.vault.get(canvas.items[0]) as AnnotationPageNormalized;
			if (!annoPage || !annoPage.items?.length) return reject('No annotation page found');

			// get annotation from vault
			const anno = panel.vault.get(annoPage.items[0]) as AnnotationNormalized;
			if (!anno || !anno.body?.length) return reject('No annotation found');

			// get body from vault
			const body = panel.vault.get(anno.body[0]) as ContentResource | { type: string; items: ContentResource[] };
			if (!body || !body.type) return reject('No content ressources found');

			if (body.type === 'Choice') {
				// If body is a choice between multiple image
				const choice = body.items[0];
				const thumb = await panel.thumbnailHelper.getBestThumbnailAtSize(choice, { width, height });
				return resolve(thumb.best.id);
			} else {
				const thumb = await panel.thumbnailHelper.getBestThumbnailAtSize(anno, { width, height });

				return resolve(thumb.best.id);
			}
		});
	};

	const onImageLoad = (e: Event) => {
		const target = e.target as HTMLImageElement; // Cast target to HTMLImageElement
		if (!target) return;

		naturalWidth = target.naturalWidth;
		naturalHeight = target.naturalHeight;
		emitEvent('rf-canvas-load');

		dispatch('load', { naturalWidth, naturalHeight, meta });
	};

	const loadInternalManifest = async () => {
		if (parseBool(interactive) && src) {
			const internalManifest = await createInternalManifest(src);
			manifestId = internalManifest.manifestId;
			canvasId = internalManifest.canvasId;
		}
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		if (interactiveMode !== 'keydown') return;

		if (e.code === 'Space') {
			e.preventDefault();
			canInteract = true;
		}
	};

	const handleKeyUp = (e: KeyboardEvent) => {
		if (interactiveMode !== 'keydown') return;

		if (e.code === 'Space') {
			canInteract = false;
		}
	};

	const onInspectorButtonClick = () => {
		if (inspectorStore.inspectorMode === 'canvas' || !inspectorStore.isVisible) {
			inspectorStore.openInspector(meta);
		}

		if (inspectorStore.inspectorMode === 'grid' || inspectorStore.inspectorMode === 'light-table') {
			inspectorStore.removeSelectedCanvas(inspectorKey);
		}
	};
	/**
	 * Hooks
	 */

	onMount(() => {
		canvasPanel?.withAtlas(onAtlas);
		canvasPanel?.whenReady(onReady);

		if (loading === 'lazy') {
			observer = new IntersectionObserver(onObserver, {
				rootMargin: '0px 0px 200px 0px',
			});
			const el = get(element);
			if (el) observer.observe(el);
		}

		forEachSlottedElement(async (el: any) => {
			if (el.tagName === 'RF-CANVAS-ANNOTATION') {
				console.log('Canvas Annotation found in canvas panel', el);

				const panel = await waitUntilAvailable(() => canvasPanel);
				// else check wait for canvas panel
				if (!panel) return;

				el.setCanvasPanel(canvasPanel);

				el.getAnnotation(canvasId, canvasPanel);
			}
		});
		isReady = true;
	});

	onDestroy(() => {
		observer?.disconnect();
	});

	$effect(() => {
		if (iiifContent || manifestId || src) refresh();

		if (fit === 'auto' || fit === 'contain' || fit === 'cover') refresh();

		if (src) loadInternalManifest();

		if (interactiveMode === 'keydown') canInteract = false;
		else canInteract = true;
	});

	/**
	 * Exports
	 */

	export { canvasPanel, meta, getCanvasSize, isInteractive, naturalWidth, naturalHeight };
</script>

<!-- Listen for mousemove on the window -->
<svelte:window onkeydown={handleKeyDown} onkeyup={handleKeyUp} />

<figure
	bind:this={$element}
	class={getClasses('rf-canvas')}
	part="rf-canvas"
	style={cssVariables({ '--aspect-ratio': getAspectRatio(width || naturalWidth, height || naturalHeight) })}
	aria-describedby={getLegendId()}
	data-inspector-key={inspectorKey}
>
	{#if isInteractive && render}
		<canvas-panel
			class="rf-canvas__panel"
			part="rf-canvas__panel"
			bind:this={canvasPanel}
			{interactive}
			{debug}
			move-events={moveEvents}
			granular-move-events={granularMoveEvents}
			responsive={getResponsiveAttribute()}
			viewport={getViewportAttribute()}
			canvas-id={getLazyAttribute(canvasId)}
			choice-id={getLazyAttribute(choiceId)}
			iiif-content={getLazyAttribute(iiifContent)}
			manifest-id={getLazyAttribute(manifestId)}
			onmedia-displayed={onMediaDisplayed}
			onworld-ready={onWorldReady}
			oncanvas-change={onCanvasChange}
			{region}
		></canvas-panel>
	{:else if src}
		<img onload={onImageLoad} class="rf-canvas__image" part="rf-canvas__image" {src} alt="" {width} {height} {loading} />
	{/if}

	<slot></slot>
	{#if !parseBool(inspectorHide)}
		{#if (inspectorStore.isEnabled && !inInspector) || (inInspector && inspectorStore.inspectorMode !== 'canvas')}
			<RfInspectorControl
				icon={inspectorStore.inspectorMode === 'canvas' || !inspectorStore.isVisible ? 'look-up' : 'minus'}
				ariaLabel={inInspector ? 'Remove from inspector' : 'View in inspector'}
				ariaHaspopup="dialog"
				ariaControls="rf-inspector"
				onclick={onInspectorButtonClick}
			/>
		{/if}
	{/if}
</figure>

<style lang="scss" global>
	.rf-canvas {
		position: relative;
		display: flex;
		flex-flow: column;
		margin: 0;
		height: var(--rf-canvas-height, 512px);
		max-height: var(--rf-canvas-max-height, unset);
		overflow: hidden;

		&--auto {
			height: var(--rf-canvas-height, auto);
		}

		&__panel {
			display: block;
			width: 100%;
			--atlas-container-width: 100%;
			--atlas-background: var(--rf-primary-color, black);
			aspect-ratio: var(--aspect-ratio);
			.no-events & {
				pointer-events: none !important;
			}

			.rf-canvas--auto & {
				height: var(--rf-canvas-panel-height, auto);
				--atlas-container-height: auto;
			}

			.rf-canvas--contain &,
			.rf-canvas--cover & {
				height: var(--rf-canvas-panel-height, 100%);
				--atlas-container-height: 100%;
			}
		}

		&__image {
			display: block;
			width: 100%;
			background-color: var(--rf-primary-color, black);

			.rf-canvas--auto & {
				height: auto;
			}

			.rf-canvas--contain & {
				height: 100%;
				object-fit: contain;
			}
			.rf-canvas--cover & {
				height: 100%;
				object-fit: cover;
			}
		}

		.rf-inspector-control {
			position: absolute;
			right: 8px;
			bottom: 8px;
			z-index: 11;
			--rf-inspector-control-border-radius: 100%;
			--rf-inspector-control-width: 30px;
			--rf-inspector-control-height: 30px;
			--rf-inspector-control-bg-color: rgba(15, 15, 15, 0.5);
			--rf-inspector-control-hover-color: rgba(15, 15, 15, 0.7);
			--rf-inspector-control-border-color: rgba(20, 20, 20, 0.5);
		}

		&.in-inspector {
			.rf-inspector-control {
				transition: opacity 0.2s ease-in-out;
				opacity: 0;
			}

			&:hover {
				.rf-inspector-control {
					opacity: 1;
				}
			}

			:host-context(rf-light-table-element:hover) & {
				.rf-inspector-control {
					opacity: 1;
				}
			}
		}

		&:hover {
			::slotted(rf-legend[hover='true']) {
				opacity: 1 !important;
			}
		}

		::slotted(rf-legend) {
			z-index: 10;
			bottom: 0;
			transition: opacity ease-out 0.3s;
		}

		&.no-legend {
			::slotted(rf-legend) {
				display: none;
			}
		}

		::slotted(rf-legend[hover='true']) {
			opacity: 0;
		}
		::slotted(rf-legend[absolute='true']) {
			position: absolute;
		}

		::slotted(rf-legend[right='true']) {
			right: 0;
		}

		::slotted(rf-legend[left='true']) {
			left: 0;
		}

		::slotted(rf-legend[top='true']) {
			top: 0;
		}

		::slotted(rf-legend[center='true']) {
			left: 50%;
			transform: translateX(-50%); /* Only offset vertically */
		}

		::slotted(rf-legend[full='true']) {
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
		}
	}
</style>
